import { useEffect, useState } from 'react';
import { Button, Flex, Text } from '@nula/core-ui';
import { useIsCapturing } from '@/lib/recordings/use-is-capturing';
import { useAppUpdate } from '@/lib/updater/use-app-update';
import shared from '../_shared.module.css';
import styles from './updates-tab.module.css';

function statusLine(state: string, progress: number, availableVersion: string | null) {
  switch (state) {
    case 'checking':
      return 'Checking for updates…';
    case 'available':
      return `Version ${availableVersion} is available.`;
    case 'up-to-date':
      return 'Nula is up to date.';
    case 'downloading':
      return `Downloading… ${progress}%`;
    case 'ready':
      return 'Update installed. Restarting…';
    case 'blocked-by-recording':
      return 'Finish your recording before installing this update.';
    case 'error':
      return 'Could not check for updates. Check your connection and try again.';
    default:
      return null;
  }
}

/**
 * The running binary's version, which CI patches per channel — package.json
 * still carries the unsuffixed base and would hide the staging suffix.
 */
function useCurrentVersion(): string | null {
  const [version, setVersion] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { getVersion } = await import('@tauri-apps/api/app');
        const v = await getVersion();
        if (!cancelled) setVersion(v);
      } catch {
        if (!cancelled) setVersion(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return version;
}

export function UpdatesTab() {
  const currentVersion = useCurrentVersion();
  const isRecording = useIsCapturing();
  const { state, availableVersion, notes, progress, check, install } = useAppUpdate({ isRecording });

  const busy = state === 'checking' || state === 'downloading' || state === 'ready';
  const status = statusLine(state, progress, availableVersion);

  return (
    <Flex vertical gap="lg">
      <Flex vertical gap="xs">
        <Text size="md" className={styles.updatesSectionLabel}>Updates</Text>
        <Text size="sm" type="secondary">Keep Nula up to date with the latest fixes and features.</Text>
      </Flex>

      <Flex vertical gap="sm">
        <Text className={shared.sectionGroupLabel}>Current Version</Text>
        <Flex align="center" justify="space-between" gap="md">
          <Flex vertical gap="xs" className={styles.updatesRowBody}>
            <Text size="sm" data-testid="updates-current-version">
              {currentVersion ? `Nula ${currentVersion}` : 'Nula'}
            </Text>
            {status && <Text size="xs" type="secondary">{status}</Text>}
          </Flex>
          <Button size="sm" variant="default" onClick={check} disabled={busy}>
            Check for Updates
          </Button>
        </Flex>
      </Flex>

      {(state === 'available' || state === 'blocked-by-recording') && (
        <Flex vertical gap="sm">
          <Text className={shared.sectionGroupLabel}>Version {availableVersion}</Text>
          {notes && (
            <Text size="xs" type="secondary" className={styles.updatesNotes}>{notes}</Text>
          )}
          <Flex align="center" justify="space-between" gap="md">
            <Flex vertical gap="xs" className={styles.updatesRowBody}>
              <Text size="xs" type="secondary">
                {isRecording
                  ? 'Installing restarts Nula, which would end your recording.'
                  : 'Nula will restart to finish installing.'}
              </Text>
            </Flex>
            <Button size="sm" variant="primary" onClick={install} disabled={isRecording}>
              Install and Restart
            </Button>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
}
